// Pure display rules shared by SubmissionList and SubmissionDetail.

import { dayInTz } from '@/features/staff/attendanceModel';

/**
 * Submitter label. The server LEFT JOINs trackizy_users to supply
 * submitted_by_name; a deleted user leaves it null, so fall back to a short
 * id rather than a raw UUID (the convention used across alerts and safety).
 */
export function submitterLabel(submission, t) {
  const name = typeof submission?.submitted_by_name === 'string' ? submission.submitted_by_name.trim() : '';
  if (name) return name;
  const id = submission?.submitted_by;
  if (!id) return '—';
  return t('forms.submissions.unknownUser', 'User #{{short}}', { short: String(id).slice(0, 8) });
}

/**
 * A media answer is only renderable when it points somewhere the browser can
 * fetch. Staff app builds before the upload fix stored the phone's own
 * file://‑style URI, which renders as a broken image; those bytes were never
 * uploaded and are unrecoverable, so the UI must say so instead.
 */
export function isRenderableMediaUrl(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v) return false;
  return v.startsWith('/') || /^https?:\/\//i.test(v) || v.startsWith('data:');
}

/** Field heading: templates can be saved with a blank label (legacy). */
export function fieldHeading(field) {
  const label = typeof field?.label === 'string' ? field.label.trim() : '';
  return label || field?.id || '';
}

/**
 * Which parent a submission hangs off. `free` (no parent at all) is the common
 * case for a visit or a form a worker fills without a dispatch, so it is a
 * first-class filter value — the Vehicle filter can never reach those rows,
 * because a free-floating submission has no vehicle.
 */
export function submissionSource(submission) {
  if (submission?.work_id) return 'work';
  if (submission?.work_stop_id) return 'stop';
  if (submission?.dispatch_job_id) return 'legacy_job';
  if (submission?.route_stop_id) return 'legacy_route_stop';
  return 'free';
}

export const SUBMISSION_SOURCES = ['work', 'stop', 'free', 'legacy_job', 'legacy_route_stop'];

export function submissionSourceLabel(source, t) {
  switch (source) {
    case 'work': return t('forms.submissions.sourceWork', 'Work');
    case 'stop': return t('forms.submissions.sourceStop', 'Stop');
    case 'legacy_job': return t('forms.submissions.sourceJob', 'Job (legacy)');
    case 'legacy_route_stop': return t('forms.submissions.sourceRouteStop', 'Route stop (legacy)');
    default: return t('forms.submissions.sourceFree', 'Free-floating');
  }
}

export function submittedAtMs(submission) {
  const ms = new Date(submission?.occurred_at || submission?.created_at).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Filters the loaded submissions. `from`/`to` are inclusive calendar days,
 * compared in the ORG timezone — the same zone the Date column renders in, so
 * a row can never display a date the filter disagrees with.
 */
export function filterSubmissions(submissions, filters, tz) {
  const { formId = '', deviceId = '', source = '', submittedBy = '', from = '', to = '' } = filters || {};
  return (submissions || []).filter((s) => {
    if (formId && s.form_id !== formId) return false;
    if (deviceId && String(s.device_id) !== String(deviceId)) return false;
    if (source && submissionSource(s) !== source) return false;
    if (submittedBy && String(s.submitted_by) !== String(submittedBy)) return false;
    if (from || to) {
      const day = dayInTz(s.occurred_at || s.created_at, tz);
      if (!day) return false;
      if (from && day < from) return false;
      if (to && day > to) return false;
    }
    return true;
  });
}

/**
 * Sorts by one column. Text compares case-insensitively; `date` compares the
 * instant, never its formatted string (which would sort "9/20" before "9/3").
 */
export function sortSubmissions(rows, { key, dir }, resolvers) {
  if (!key) return rows;
  const sign = dir === 'desc' ? -1 : 1;
  const value = (row) => (key === 'submittedAt' ? submittedAtMs(row) : String(resolvers[key]?.(row) ?? '').toLowerCase());
  return [...rows].sort((a, b) => {
    const va = value(a); const vb = value(b);
    if (va < vb) return -sign;
    if (va > vb) return sign;
    return 0;
  });
}

/** Distinct submitters present in the loaded set, for the person filter. */
export function submitterOptions(submissions, t) {
  const byId = new Map();
  for (const s of submissions || []) {
    if (!s?.submitted_by) continue;
    const id = String(s.submitted_by);
    if (!byId.has(id)) byId.set(id, submitterLabel(s, t));
  }
  return [...byId.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
